import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdPageable, Paginator } from '@bindoc-projects/platform-lib/data';
import {
  BdCompanyContextService,
  BdGrantsService,
  BdShareablesService,
  revokeGrantConfirmMessage,
  ShareableGrantFull,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { BD_ARCHIVE_SHAREABLE_OPENER } from '../../archive-shareable/archive-shareable.token';
import {
  BD_DANGER_MENU_ITEM_CSS,
  BD_DANGER_MENU_ITEM_STYLE,
} from '../../info-menu/danger-menu-item';
import {
  BdEditLabelsDialogComponent,
  EditLabelsDialogData,
  EditLabelsDialogResult,
} from '../../edit-labels-dialog/edit-labels-dialog.component';
import { BdShareableSelectService } from '../../select/global-search/shareable-select.service';
import { BdWorkspaceLinkService } from '../../workspace-link/workspace-link.service';
import {
  BdSharePermission,
  BdSharePermissionHelper,
  ShareableLabel,
  ShareableObj,
  ShareableType,
  SHAREABLE_TYPE_DISPLAY_NAME,
} from '@bindoc-projects/resource-domain-lib';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import {
  catchError,
  EMPTY,
  expand,
  forkJoin,
  map,
  Observable,
  of,
  reduce,
  skip,
  Subject,
  switchMap,
  take,
} from 'rxjs';

import { BdFolderTableComponent } from '../folder-table/folder-table.component';
import {
  compareFolderItemsByField,
  FolderTableSortBy,
  FolderTableSortChange,
  FolderTableSortMode,
} from '../folder-table/folder-table.logic';
import { BdGrantDialogComponent } from '../grant-dialog/grant-dialog.component';
import { BdMoveDialogComponent } from '../move-dialog/move-dialog.component';
import { BdPermissionsWithDetailsDialogComponent } from '../permission-detail-dialog/permissions-with-details-dialog.component';
import { ItemSelectionData, SelectedItem } from '../dialog.models';
import {
  FolderItem,
  InlineNotice,
  ItemNavigateEvent,
  SHAREABLE_LIST_BROWSE,
  ShareableListPresentation,
} from '../shareables-overview.models';

/** Everything a recent-list fetch depends on, resolved when the fetch starts. */
interface RecentRequestParams {
  readonly types: ShareableType[] | undefined;
  readonly labels: string[] | undefined;
  /** Trimmed name query — server-side for own resources, client-side for grants. */
  readonly query: string | undefined;
  readonly companyId: string | undefined;
}

type RecentLoadResult =
  | {
      readonly ok: true;
      /** Every matching own resource — all server pages, already concatenated. */
      readonly shareables: readonly ShareableObj[];
      readonly grants: ShareableGrantFull[];
      readonly params: RecentRequestParams;
    }
  | { readonly ok: false };

@Component({
  selector: 'bd-recent-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!--
      No filter form above the table: the Label filter lives in the Name column
      header, next to the Typ filter in its own — one place to narrow the list,
      and the rows start at the top of the tab.
    -->
    <bd-folder-table
      [items]="recentItems()"
      [loading]="loading()"
      [presentation]="presentation()"
      [selection]="selection()"
      [notice]="notice()"
      [showType]="showType()"
      [typeFilter]="typeFilterAvailable()"
      [labelFilter]="presentation().showLabelFilter"
      [labelFilterOptions]="availableLabels()"
      [labelFilterValue]="selectedLabel()"
      [sortMode]="sortMode()"
      [sortBy]="'modifiedDate'"
      [sortField]="sortField()"
      [sortOrder]="sortOrder()"
      [pageSize]="pageSize()"
      [fillHeight]="fillHeight()"
      [menuItemsProvider]="menuItemsProvider"
      [emptyMessage]="emptyMessage()"
      [emptyDetail]="emptyDetail()"
      [emptyIcon]="emptyIcon()"
      (itemClick)="onItemClick($event)"
      (labelClick)="onLabelClick($event)"
      (labelFilterChange)="onLabelSelectionChange($event)"
      (sortChange)="onSortChange($event)"
      (selectionChange)="onTableSelectionChange($event)"
    ></bd-folder-table>
    <p-confirmdialog />
  `,
  host: {
    class: 'flex flex-col',
    // Mirrors bd-folder-table: only in fill mode is this a stretching, freely
    // shrinking flex child. See `fillHeight`.
    '[class.flex-auto]': 'fillHeight()',
    '[class.min-h-0]': 'fillHeight()',
  },
  styles: [BD_DANGER_MENU_ITEM_CSS],
  imports: [BdFolderTableComponent, ConfirmDialog],
  providers: [ConfirmationService, BdConfirmDeleteService],
})
export class BdRecentTabComponent implements OnInit {
  /** Rows per request while walking the own-resources pages. */
  private static readonly PAGE_SIZE = 200;
  /**
   * Hard cap on the number of pages a single walk fetches (25 × 200 = 5000
   * rows). Runaway guard for pathological accounts and misbehaving mocks — not
   * an expected limit, so don't treat it as the list's real ceiling.
   */
  private static readonly MAX_PAGES = 25;

  public readonly shareableType = input<ShareableType | undefined>(undefined);
  /**
   * Multi-type filter; takes precedence over {@link shareableType} when set.
   * Lets a picker consumer restrict the list to several shareable types at
   * once.
   */
  public readonly shareableTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );
  /** Show a "Typ" column — useful when more than one shareable type is listed. */
  public readonly showType = input(false);
  /**
   * Browse or pick — see {@link ShareableListPresentation}. The table flags are
   * forwarded as they are; `showLabelFilter` and `showActions` are this tab's
   * own. With `showCheckbox`, row clicks toggle the selection instead of
   * navigating (see {@link onItemClick}).
   */
  public readonly presentation = input<ShareableListPresentation>(
    SHAREABLE_LIST_BROWSE,
  );
  /**
   * Forwarded to {@link BdFolderTableComponent.selection} — set it to control
   * the ticks from outside (a picker whose selection outlives a reload).
   */
  public readonly selection = input<readonly FolderItem[] | undefined>(
    undefined,
  );
  /** Whether the table columns can be sorted (server-side). */
  public readonly sortable = input(true);
  /**
   * This tab sorts server-side across its paged requests, so the table hands
   * every header click back via `sortChange` instead of reordering itself.
   */
  protected readonly sortMode = computed<FolderTableSortMode>(() =>
    this.sortable() ? 'parent' : 'fixed',
  );
  /** Page size for the pager; `undefined` shows the list without a pager. */
  public readonly pageSize = input<number | undefined>(25);
  /**
   * Caps the list to the most recent N entries; `undefined` shows all. A set
   * limit also skips the multi-page walk over own resources (see
   * {@link _ownShareables}).
   */
  public readonly limit = input<number | undefined>(undefined);
  /**
   * Forwarded to {@link BdFolderTableComponent.fillHeight} — see there. `false`
   * for a list stacked with other sections on a page-level scroll (Home).
   */
  public readonly fillHeight = input(true);
  /**
   * Filters the list by name (server-side for own resources, client-side for
   * grants — see {@link reload}). Changing it reloads on its own; the same goes
   * for {@link shareableType} / {@link shareableTypes} (see the constructor).
   */
  public readonly searchQuery = input<string | undefined>(undefined);
  public readonly itemNavigate = output<ItemNavigateEvent>();
  public readonly selectionChange = output<FolderItem[]>();

  public readonly recentItems = signal<FolderItem[]>([]);
  public readonly loading = signal(false);
  public readonly notice = signal<InlineNotice | null>(null);
  /** Currently applied label filter (the exact, case-sensitive label text). */
  public readonly selectedLabel = signal<string | null>(null);
  /**
   * Distinct label texts the user can see, feeding the Name column's filter.
   * Sorted and de-duplicated here: the list box offers them in the order it is
   * given, and the picker component that used to do both is gone.
   */
  public readonly availableLabels = signal<string[]>([]);
  public readonly sortField = signal<FolderTableSortBy>('modifiedDate');
  public readonly sortOrder = signal<1 | -1>(-1);

  /**
   * Whether the table may offer the Typ column filter — the same affordance the
   * workspace tabs have.
   *
   * It filters client-side over the rows already loaded, which is exactly right
   * here *because* the uncapped list walks every server page into memory. Under
   * {@link limit} only the first page is fetched (Home's short list), so the
   * filter would narrow that page alone and report its counts as the whole
   * truth — offer it only without a cap. A picker brings its own criteria, so
   * it is off there too.
   */
  protected readonly typeFilterAvailable = computed(
    () => this.presentation().showActions && this.limit() === undefined,
  );

  public readonly emptyMessage = computed(() =>
    this.selectedLabel() !== null || this.searchQuery()
      ? 'Keine Treffer für die aktiven Filter'
      : 'Keine kürzlich verwendeten Einträge',
  );

  /**
   * An empty search is an empty state, not an error: it stays neutral but the
   * styleguide marks the filtered case with a search icon and explains the way
   * out. Unfiltered emptiness keeps the default info glyph.
   */
  public readonly emptyIcon = computed(() =>
    this.selectedLabel() !== null ? 'pi pi-search' : '',
  );

  public readonly emptyDetail = computed(() => {
    // The filters compose — a new search keeps the Typ pick, a Typ pick keeps
    // the search — so the way out has to name every one that is narrowing the
    // list, not just the one whose empty state happens to be showing.
    const active: string[] = [];
    if (this.selectedLabel() !== null) {
      active.push('den Label-Filter');
    }
    if (this.searchQuery()?.trim()) {
      active.push('die Suche');
    }
    return active.length > 0
      ? `Setzen Sie ${active.join(' und ')} zurück, um mehr Einträge zu sehen.`
      : '';
  });

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _grantsService = inject(BdGrantsService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _dialogService = inject(DialogService);
  private readonly _confirmDeleteService = inject(BdConfirmDeleteService);
  private readonly _selectService = inject(BdShareableSelectService);
  private readonly _workspaceLinks = inject(BdWorkspaceLinkService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _archiveOpener = inject(BD_ARCHIVE_SHAREABLE_OPENER, {
    optional: true,
  });

  private readonly _grantsByItemId = new Map<string, ShareableGrantFull>();

  private readonly _folderTable = viewChild.required(BdFolderTableComponent);

  /** Fetch trigger; every emission supersedes the request in flight. */
  private readonly _reload$ = new Subject<void>();

  constructor() {
    // `switchMap` makes a newer request cancel the one in flight, so a slow
    // response for a filter the user has already moved on from can never
    // overwrite the fresh list.
    this._reload$
      .pipe(
        switchMap(() => this._request()),
        takeUntilDestroyed(),
      )
      .subscribe((result) => this._applyResult(result));

    // The filter inputs belong to the parent, which may change them while this
    // component stays alive (a picker narrowing its type filter or its search).
    // Reloading reactively is what frees consumers from re-creating the
    // component or chasing input changes with a manual `reload()` one render
    // later. `untracked` keeps the reload's own signal reads out of the
    // dependency set; the first run is the initial fetch, which is why
    // `ngOnInit` no longer starts one.
    effect(() => {
      this.searchQuery();
      this.shareableType();
      this.shareableTypes();
      untracked(() => this.reload());
    });
  }

  public readonly menuItemsProvider = (item: FolderItem): MenuItem[] => {
    if (!this.presentation().showActions || item.isRecycleBinItem) {
      return [];
    }
    const allowed = item.allowedPermissions ?? [];
    const highest =
      allowed.length > 0
        ? BdSharePermissionHelper.getHighestPermission(allowed)
        : null;
    const canUpdate =
      highest !== null && BdSharePermissionHelper.includes(highest, 'UPDATE');
    const canDelete =
      highest !== null && BdSharePermissionHelper.includes(highest, 'DELETE');

    if (this._grantsByItemId.has(item.id)) {
      // Grants: parentFolderId is in the grantor's tree, so "Im Ordner öffnen"
      // does not apply here.
      return [
        {
          label: 'Öffnen',
          icon: 'pi pi-external-link',
          command: () => this.onItemClick(item),
        },
        {
          label: 'In neuem Tab öffnen',
          icon: 'pi pi-window-maximize',
          command: () => this._openInNewTab(item),
        },
        {
          label: 'Labels bearbeiten',
          icon: 'pi pi-tag',
          visible: canUpdate,
          command: () => this._editLabels(item),
        },
        {
          label: 'Erneut freigeben',
          icon: 'pi pi-share-alt',
          // Sharing requires DELETE — the grant dialog's share-preview step
          // 403s for weaker permissions, so don't offer it then (BDA-5507).
          visible: canDelete,
          command: () => this._regrantItem(item, highest),
        },
        {
          label: 'Zugriff',
          icon: 'pi pi-key',
          visible: canUpdate,
          command: () => this._showPermissions(item),
        },
        {
          label: 'Freigabe entfernen',
          icon: 'pi pi-trash',
          ...BD_DANGER_MENU_ITEM_STYLE,
          command: () => this._revokeGrant(item),
        },
      ];
    }

    return [
      {
        label: 'Öffnen',
        icon: 'pi pi-external-link',
        command: () => this.onItemClick(item),
      },
      {
        label: 'In neuem Tab öffnen',
        icon: 'pi pi-window-maximize',
        command: () => this._openInNewTab(item),
      },
      {
        label: 'Im Ordner öffnen',
        icon: 'pi pi-folder-open',
        // Hidden in an app with no reachable workspace (BD_WORKSPACE_LOCATION).
        visible: !!item.parentFolderId && this._workspaceLinks.available,
        command: () => this._openInWorkspaceFolder(item),
      },
      {
        label: 'Labels bearbeiten',
        icon: 'pi pi-tag',
        visible: canUpdate,
        command: () => this._editLabels(item),
      },
      {
        label: 'Verschieben',
        icon: 'pi pi-arrow-right',
        command: () => this._moveItem(item),
      },
      {
        label: 'Freigeben',
        icon: 'pi pi-share-alt',
        // Sharing requires DELETE — see BDA-5507.
        visible: canDelete,
        command: () => this._shareItem(item),
      },
      {
        label: 'Zugriff',
        icon: 'pi pi-key',
        visible: canUpdate,
        command: () => this._showPermissions(item),
      },
      {
        label: 'Löschen',
        icon: 'pi pi-trash',
        ...BD_DANGER_MENU_ITEM_STYLE,
        visible: canDelete,
        command: () => this._deleteItem(item),
      },
    ];
  };

  ngOnInit(): void {
    // No initial `reload()` here — the constructor's effect fires it once the
    // inputs are in place, and doing both would fetch the list twice.
    this._loadAvailableLabels();
    this._companyContext.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.reload();
        this._loadAvailableLabels();
      });
  }

  /**
   * `shareableTypes` (multi-type) takes precedence over the single-type
   * `shareableType` input when both are set.
   */
  private _effectiveTypes(): ShareableType[] | undefined {
    const multi = this.shareableTypes();
    if (multi) {
      return [...multi];
    }
    const single = this.shareableType();
    return single ? [single] : undefined;
  }

  /**
   * Refetches the list with the current filters, superseding a request still in
   * flight (own resources may take several requests — see
   * {@link _ownShareables}). Callers may invoke it directly after changing the
   * underlying data; filter *inputs* reload on their own (see the constructor).
   */
  public reload(): void {
    this.loading.set(true);
    this.notice.set(null);
    this._reload$.next();
  }

  /** Snapshot of the filters a fetch runs with, taken when it starts. */
  private _requestParams(): RecentRequestParams {
    const selectedLabel = this.selectedLabel();
    return {
      types: this._effectiveTypes(),
      labels: selectedLabel ? [selectedLabel] : undefined,
      query: this.searchQuery()?.trim() || undefined,
      companyId: this._companyContext.currentCompanyId,
    };
  }

  private _request(): Observable<RecentLoadResult> {
    const params = this._requestParams();
    return forkJoin({
      // Own resources are paginated server-side, so push the label filter to
      // the API to match across the full set (not just one page) — and walk
      // every page, see `_ownShareables`.
      shareables: this._ownShareables(params),
      grants: this._grantsService.myGrants('READ', params.companyId),
    }).pipe(
      map(({ shareables, grants }): RecentLoadResult => ({
        ok: true,
        shareables,
        grants,
        params,
      })),
      catchError((): Observable<RecentLoadResult> => of({ ok: false })),
    );
  }

  /**
   * Loads **every** own resource matching the current filters by walking the
   * server pages, not just the first one.
   *
   * The flat list pages client-side (see {@link BdFolderTableComponent}) and the
   * table's header checkbox deliberately selects all results across all pages
   * (Finder semantics) — so the loaded set has to actually *be* all results.
   * Fetching a single page instead made select-all mean "the first page-worth",
   * the pager total lie, and search hits past that page unreachable.
   *
   * Every request carries the same query, sort, type and label filters and only
   * advances `pageIndex`; the stable server-side sort is what makes the walk
   * coherent. Being one `Observable` inside the request `switchMap`, a newer
   * reload cancels a walk still in flight.
   */
  private _ownShareables(
    params: RecentRequestParams,
  ): Observable<readonly ShareableObj[]> {
    const sort = [Paginator.sortExpression(this.sortField(), this.sortOrder())];
    const fetchPage = (
      pageIndex: number,
      pageSize: number,
    ): Observable<BdPageable<ShareableObj>> =>
      this._resourcesService.myShareables(
        Paginator.of({ pageSize, pageIndex, query: params.query, sort }),
        params.types,
        [ShareableType.FOLDER],
        params.companyId,
        params.labels,
      );

    const limit = this.limit();
    if (limit != null) {
      // Fast path for capped consumers (Home lists the 20 most recent entries):
      // the merged list is sliced to `limit` anyway, so walking every page would
      // only spend requests on rows that are thrown away.
      return fetchPage(0, Math.max(50, limit)).pipe(
        map((page) => page.content),
      );
    }

    return fetchPage(0, BdRecentTabComponent.PAGE_SIZE).pipe(
      expand((page, index) => {
        // Only continue when the page positively says more rows exist. Written
        // as a "keep going" check on purpose: a response missing the Spring
        // page fields yields `NaN < undefined` → false, so the walk stops
        // instead of spinning.
        const hasMore =
          !page.last &&
          page.content.length > 0 &&
          page.number + 1 < page.totalPages;
        // `index` is 0-based over the pages flowing through `expand`, so
        // `index + 1` is the number of pages fetched so far. The cap is a
        // runaway guard for pathological accounts and misbehaving mocks, not an
        // expected limit — a real account never hits it.
        const withinBound = index + 1 < BdRecentTabComponent.MAX_PAGES;
        return hasMore && withinBound
          ? fetchPage(page.number + 1, BdRecentTabComponent.PAGE_SIZE)
          : EMPTY;
      }),
      reduce(
        (all: ShareableObj[], page) => all.concat(page.content),
        [] as ShareableObj[],
      ),
    );
  }

  private _applyResult(result: RecentLoadResult): void {
    if (!result.ok) {
      this.notice.set({
        severity: 'error',
        title: 'Die zuletzt verwendeten Einträge konnten nicht geladen werden',
        message:
          'Bitte versuchen Sie es in einem Moment erneut oder kehren Sie später zurück.',
      });
      this.loading.set(false);
      return;
    }
    this.recentItems.set(
      this._merge(
        result.shareables,
        this._filterGrants(result.grants, result.params),
      ),
    );
    this.loading.set(false);
  }

  /**
   * Grants come back unpaginated, and the grants endpoint supports neither the
   * label nor the query parameter — so the filters the API applied to own
   * resources are re-applied here by hand.
   */
  private _filterGrants(
    grants: ShareableGrantFull[],
    params: RecentRequestParams,
  ): ShareableGrantFull[] {
    const { types, labels, query } = params;
    let filtered = types
      ? grants.filter((g) => types.includes(g.shareableReference.type))
      : grants;
    // Exact, case-sensitive label match — same semantics as the API's `label`.
    if (labels) {
      filtered = filtered.filter((g) =>
        g.shareableReference.labels?.some((l) => labels.includes(l.text)),
      );
    }
    // Name-contains, case-insensitive (unlike the server-side `query` param).
    if (query) {
      const needle = query.toLowerCase();
      filtered = filtered.filter((g) =>
        g.shareableReference.name?.toLowerCase().includes(needle),
      );
    }
    return filtered;
  }

  private _loadAvailableLabels(): void {
    const types = this._effectiveTypes();
    const companyId = this._companyContext.currentCompanyId;
    this._resourcesService
      .allLabels(types, [ShareableType.FOLDER], companyId)
      .pipe(take(1))
      .subscribe({
        next: (labels) =>
          this.availableLabels.set(
            [...new Set(labels)].sort((a, b) => a.localeCompare(b)),
          ),
        error: () => this.availableLabels.set([]),
      });
  }

  public onItemClick(item: FolderItem): void {
    if (this.presentation().showCheckbox) {
      // Row-click toggle for checkbox mode. The table owns the merge and the
      // controlled/uncontrolled split, so this is the same interaction as
      // clicking the row's checkbox.
      this._folderTable().toggle(item);
      return;
    }
    this.itemNavigate.emit({ id: item.id, type: item.type, name: item.name });
  }

  /**
   * Selection change coming from the table, which reports the complete selection
   * — including, under a controlled {@link selection}, the picks made in another
   * scope. Nothing left to do here but hand it on.
   */
  public onTableSelectionChange(items: FolderItem[]): void {
    this.selectionChange.emit(items);
  }

  /** A label tag clicked inside a table row applies it as the active filter. */
  public onLabelClick(label: ShareableLabel): void {
    if (this.selectedLabel() === label.text) {
      return;
    }
    this.selectedLabel.set(label.text);
    this.reload();
  }

  /**
   * A label picked (or cleared) in the Name column's filter applies / removes
   * it. The table reports `null` for "cleared"; `undefined` is accepted too so
   * the call stays valid for callers that never had a value.
   */
  public onLabelSelectionChange(label: string | null | undefined): void {
    const next = label ?? null;
    if (this.selectedLabel() === next) {
      return;
    }
    this.selectedLabel.set(next);
    this.reload();
  }

  public onSortChange(change: FolderTableSortChange): void {
    this.sortField.set(change.field);
    this.sortOrder.set(change.order);
    this.reload();
  }

  private _showPermissions(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Zugriff',
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService.open(
      BdPermissionsWithDetailsDialogComponent,
      dialogConfig,
    );
  }

  private _moveItem(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Verschieben',
      data: { items: [this._toSelectedItem(item)] },
    };
    this._dialogService
      .open(BdMoveDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.reload();
        }
      });
  }

  private _shareItem(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Freigeben',
      data: { items: [this._toSelectedItem(item)] },
    };
    this._dialogService
      .open(BdGrantDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result?.granted) {
          this.reload();
        }
      });
  }

  private _regrantItem(
    item: FolderItem,
    initialPermission: BdSharePermission | null,
  ): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Erneut freigeben',
      data: {
        items: [this._toSelectedItem(item)],
        initialPermission: initialPermission ?? undefined,
      },
    };
    this._dialogService
      .open(BdGrantDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result) => {
        if (result?.granted) {
          this.reload();
        }
      });
  }

  private _openInNewTab(item: FolderItem): void {
    this._selectService.openInNewTab(item.id, item.type);
  }

  private _openInWorkspaceFolder(item: FolderItem): void {
    if (!item.parentFolderId) {
      return;
    }
    // The recent list is flat, so a shareable may live in a company group. The
    // API has no folder→group field, so resolve the owning group from the
    // folder's scope-root and forward it; fall back to the user scope when the
    // folder isn't in a group. The receiver highlights the group in the sidebar
    // and lands the folder table on the deep-linked shareable.
    this._resourcesService
      .resolveFolderGroup(
        item.parentFolderId,
        this._companyContext.currentCompanyId,
      )
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((group) => {
        const queryParams = group
          ? { tab: 'group', groupId: group.id, shareableId: item.id }
          : { tab: 'shareables', shareableId: item.id };
        this._workspaceLinks.open(queryParams);
      });
  }

  private _editLabels(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig<EditLabelsDialogData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: `Labels für „${item.name}“ bearbeiten`,
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService
      .open(BdEditLabelsDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result: EditLabelsDialogResult | null) => {
        if (!result) {
          return;
        }
        this.recentItems.update((items) =>
          items.map((it) =>
            it.id === item.id && it.type === item.type
              ? { ...it, labels: result.labels }
              : it,
          ),
        );
      });
  }

  private _deleteItem(item: FolderItem): void {
    const opener = this._archiveOpener;
    const confirmed$ =
      item.isFolder || !opener
        ? this._confirmDeleteService.confirm({
            itemType: item.isFolder ? 'Ordner' : item.typeLabel,
            itemName: item.name,
          })
        : opener({
            shareableType: item.type,
            shareableId: item.id,
            shareableName: item.name,
          });
    confirmed$.subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this._resourcesService
        .archive(item.type, item.id)
        .pipe(take(1))
        .subscribe(() => {
          this.recentItems.update((items) =>
            items.filter((i) => i.id !== item.id),
          );
        });
    });
  }

  private _revokeGrant(item: FolderItem): void {
    const grant = this._grantsByItemId.get(item.id);
    if (!grant) {
      return;
    }
    this._confirmDeleteService
      .confirm({
        customHeader: 'Freigabe entfernen?',
        customMessage: revokeGrantConfirmMessage(item.name, grant.grantee),
        customAcceptLabel: 'Ja, Freigabe entfernen',
      })
      .subscribe(() => {
        this._grantsService
          .revoke(grant.shareableReference, grant.grantee)
          .pipe(take(1))
          .subscribe(() => this.reload());
      });
  }

  private _toSelectedItem(item: FolderItem): SelectedItem {
    return {
      id: item.id,
      type: item.type,
      displayName: item.name,
      typeLabel: item.typeLabel,
      modifiedDate: item.modifiedDate,
    };
  }

  private _merge(
    shareables: readonly ShareableObj[],
    grants: ShareableGrantFull[],
  ): FolderItem[] {
    const seen = new Set<string>();
    const all: FolderItem[] = [];
    this._grantsByItemId.clear();

    for (const s of shareables) {
      const key = `${s.type}:${s.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      all.push({
        id: s.id,
        type: s.type,
        isFolder: false,
        name: s.name,
        typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[s.type] ?? s.type,
        modifiedDate: s.modifiedDate?.toString(),
        parentFolderId: s.parentFolderId || undefined,
        labels: s.labels,
        allowedPermissions: s.allowedShareablePermissions,
      });
    }
    for (const g of grants) {
      const ref = g.shareableReference;
      const key = `${ref.type}:${ref.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      this._grantsByItemId.set(ref.id, g);
      all.push({
        id: ref.id,
        type: ref.type,
        // The shareables query excludes folders, but a folder can still reach
        // the user as a grant — it must stay a folder row (BDA-6454).
        isFolder: ref.type === ShareableType.FOLDER,
        name: ref.name ?? ref.id,
        typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[ref.type] ?? ref.type,
        modifiedDate: ref.modifiedDate?.toString(),
        labels: ref.labels,
        allowedPermissions: ref.allowedShareablePermissions,
      });
    }
    const sorted = this._sortItems(all);
    const max = this.limit();
    return max != null ? sorted.slice(0, max) : sorted;
  }

  /**
   * Orders the merged own-resources + grants list by the active sort column,
   * mirroring the `sort` expression forwarded to the API so the displayed
   * order stays consistent across both data sources.
   */
  private _sortItems(items: FolderItem[]): FolderItem[] {
    const field = this.sortField();
    const order = this.sortOrder();
    // Flat, ungrouped order — unlike the folder tabs this list holds no folders
    // and must mirror the API's `sort` expression exactly.
    return [...items].sort(
      (a, b) => order * compareFolderItemsByField(a, b, field),
    );
  }
}
